import styled from "@emotion/styled";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Button,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Box,
  Typography,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import {
  postWithQueryApiServices,
  putWithQueryApiServices,
  getWithQueryApiServices,
  deletewithQueryApiServices,
} from "../../../apis/api";
import { apiRoutes } from "../../../apis/apiPath";
import { errorToast, successToast } from "../../../utils/helper";
import ipRegex from "ip-regex";

const StyledInput = styled("input")(({ theme }) => ({
  backgroundColor: "white",
  borderRadius: 4,
  borderWidth: 1,
  borderStyle: "solid",
  borderColor: "#bdbdbd",
  height: 32,
  margin: "0 4px 0 12px",
  padding: "0 8px",
  flex: 1,
}));

const IpRuleBox = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  backgroundColor: "#deedf5",
  boxSizing: "border-box",
  padding: "10px",
  margin: "12px 0",
  borderRadius: 8,
}));

const IpRuleRow = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  marginBottom: "8px",
}));

const FormContainer = styled("div")(({ theme, isFormEnabled }) => ({
  maxHeight: "80vh",
  height: "92%",
  [theme.breakpoints.down("md")]: {
    height: "auto",
    maxHeight: "none",
    paddingBottom: "30px",
    overflow: "visible",
  },
  overflow: "hidden",
  pointerEvents: isFormEnabled ? "auto" : "none",
  backgroundColor: isFormEnabled ? "white" : "#f0f0f0",
  filter: !isFormEnabled ? "blur(5px)" : "none",
  transition: "filter 0.3s ease",
  borderRadius: 10,
  padding: "20px",
}));

const LoadingContainer = styled(Box)({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "200px",
  flexDirection: "column",
  gap: "16px",
});

const validateIP = (ip) => {
  const regex = ipRegex({ exact: true });
  return regex.test(ip);
};

const validateSubnet = (subnet) => {
  const regex = new RegExp(
    `^${ipRegex({ exact: true }).source}\\/(?:[0-9]|[1-2][0-9]|3[0-2])$`
  );
  return regex.test(subnet);
};

export const SecurityProfileForm = ({
  securityProfileData,
  isFormEnabled,
  setIsFormEnabled,
  setSelectedSecurityProfiles,
  effectiveTenant,
  tenantString,
}) => {
  const [ipRules, setIpRules] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const buildIpRulesQueryString = () => {
    const params = new URLSearchParams();
    
    if (securityProfileData?.securityprofileID) {
      params.append('securityprofileID', securityProfileData.securityprofileID);
    }
    
    if (effectiveTenant) {
      params.append('selectedTenant', effectiveTenant);
    }
    
    if (tenantString) {
      const cleanTenantString = tenantString.startsWith('&') ? 
        tenantString.slice(1) : tenantString;
      return `${params.toString()}&${cleanTenantString}`;
    }
    
    return params.toString();
  };

  const { 
    data: ipRulesData, 
    refetch: refetchIpRules,
    isLoading: ipRulesLoading,
    error: ipRulesError
  } = useQuery({
    queryKey: [
      "ipRules",
      securityProfileData?.securityprofileID,
      effectiveTenant,
    ],
    queryFn: async () => {
      const queryString = buildIpRulesQueryString();
      const response = await getWithQueryApiServices(
        apiRoutes.GET_IP_RULES,
        queryString
      );
      return response;
    },
    enabled: !!securityProfileData?.securityprofileID && !!effectiveTenant && isFormEnabled,
    retry: 3,
    refetchOnWindowFocus: false,
    onError: (error) => {
      errorToast('Failed to load IP rules');
    },
  });

  useEffect(() => {
    if (ipRulesData?.data && Array.isArray(ipRulesData.data)) {
      setIpRules(ipRulesData.data);
    } else {
      setIpRules([]);
    }
  }, [ipRulesData]);

  useEffect(() => {
    if (!isFormEnabled || !securityProfileData?.securityprofileID) {
      setIpRules([]);
    }
  }, [isFormEnabled, securityProfileData?.securityprofileID]);

  const addIpRule = () => {
    setIpRules((prev) => {
      const prevArray = Array.isArray(prev) ? prev : [];
      return [
        ...prevArray,
        {
          type: "single",
          ipAddress: "",
          startIP: "",
          endIP: "",
          subnet: "",
        },
      ];
    });
  };

  const removeIpRule = (index) => {
    const rule = ipRules[index];

    if (rule && rule.ipRuleID) {
      deleteIpRule.mutate(rule.ipRuleID);
    } else {
      setIpRules((prev) => {
        const prevArray = Array.isArray(prev) ? prev : [];
        const newRules = [...prevArray];
        newRules.splice(index, 1);
        return newRules;
      });
    }
  };

  const handleIpRuleChange = (name, value, index) => {
    setIpRules((prev) => {
      const prevArray = Array.isArray(prev) ? prev : [];
      const copyValue = [...prevArray];
      let workData = { ...copyValue[index], [name]: value };

      if (name === "type") {
        workData = {
          ...workData,
          ipAddress: "",
          startIP: "",
          endIP: "",
          subnet: "",
        };
      }

      copyValue[index] = workData;
      return copyValue;
    });
  };

  const validateIpRules = () => {
    const rulesArray = Array.isArray(ipRules) ? ipRules : [];

    for (let i = 0; i < rulesArray.length; i++) {
      const rule = rulesArray[i];

      if (rule.type === "single") {
        const ip = (rule.ipAddress || "").trim();
        if (!ip || !validateIP(ip)) {
          errorToast(`Invalid IP address in rule ${i + 1}`);
          return false;
        }
      } else if (rule.type === "range") {
        const start = (rule.startIP || "").trim();
        const end = (rule.endIP || "").trim();
        if (
          !start ||
          !end ||
          !validateIP(start) ||
          !validateIP(end)
        ) {
          errorToast(`Invalid IP range in rule ${i + 1}`);
          return false;
        }
      } else if (rule.type === "subnet") {
        const subnet = (rule.subnet || "").trim();
        if (!subnet || !validateSubnet(subnet)) {
          errorToast(`Invalid subnet in rule ${i + 1}`);
          return false;
        }
      }
    }
    return true;
  };

  const buildMutationQueryString = (additionalParams = {}) => {
    const params = new URLSearchParams();
    
    if (securityProfileData?.securityprofileID) {
      params.append('securityprofileID', securityProfileData.securityprofileID);
    }
    
    if (effectiveTenant) {
      params.append('selectedTenant', effectiveTenant);
    }

    Object.entries(additionalParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value);
      }
    });
    
    if (tenantString) {
      const cleanTenantString = tenantString.startsWith('&') ? 
        tenantString.slice(1) : tenantString;
      return `${params.toString()}&${cleanTenantString}`;
    }
    
    return params.toString();
  };

  const saveIpRules = async () => {
    if (!validateIpRules()) {
      return;
    }

    const rulesArray = Array.isArray(ipRules) ? ipRules : [];
    
    // Trim values on save
    const trimmedRules = rulesArray.map(rule => ({
      ...rule,
      ipAddress: (rule.ipAddress || "").trim(),
      startIP: (rule.startIP || "").trim(),
      endIP: (rule.endIP || "").trim(),
      subnet: (rule.subnet || "").trim(),
    }));

    const newRules = trimmedRules.filter((rule) => !rule.ipRuleID);
    const updatedRules = trimmedRules.filter((rule) => rule.ipRuleID);

    setIsSaving(true);
    try {
      const promises = [];
      if (newRules.length > 0) {
        promises.push(createIpRules.mutateAsync(newRules));
      }
      if (updatedRules.length > 0) {
        updatedRules.forEach((rule) => {
          promises.push(updateIpRule.mutateAsync(rule));
        });
      }

      if (promises.length > 0) {
        await Promise.all(promises);
        successToast("IP Rules saved successfully");
      } else {
        successToast("No changes to save");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const createIpRules = useMutation({
    mutationKey: ["createIpRules"],
    mutationFn: (rules) => {
      const queryString = buildMutationQueryString();
      return postWithQueryApiServices(
        apiRoutes.CREATE_IP_RULES,
        queryString,
        { rules }
      );
    },
    onSuccess: () => {
      refetchIpRules();
      successToast("IP Rules added successfully");
    },
    onError: (error) => {
      errorToast("Failed to add IP rules");
    },
  });

  const updateIpRule = useMutation({
    mutationKey: ["updateIpRule"],
    mutationFn: (rule) => {
      const queryString = buildMutationQueryString({ ipRuleID: rule.ipRuleID });
      return putWithQueryApiServices(
        apiRoutes.UPDATE_IP_RULE,
        queryString,
        rule
      );
    },
    onSuccess: () => {
      refetchIpRules();
      successToast("IP Rule updated successfully");
    },
    onError: (error) => {
      errorToast("Failed to update IP rule");
    },
  });

  const deleteIpRule = useMutation({
    mutationKey: ["deleteIpRule"],
    mutationFn: (ipRuleID) => {
      const queryString = buildMutationQueryString();
      return deletewithQueryApiServices(
        apiRoutes.DELETE_IP_RULE,
        queryString,
        ipRuleID
      );
    },
    onSuccess: () => {
      refetchIpRules();
      successToast("IP Rule deleted successfully");
    },
    onError: (error) => {
      errorToast("Failed to delete IP rule");
    },
  });

  const renderIpRuleInputs = (rule, index) => {
    if (!rule) return null;

    switch (rule.type) {
      case "single":
        return (
          <StyledInput
            placeholder="Enter IP Address (e.g., 192.168.1.100)"
            value={rule.ipAddress || ""}
            onChange={(e) =>
              handleIpRuleChange("ipAddress", e.target.value, index)
            }
            disabled={!isFormEnabled || isSaving}
          />
        );
      case "range":
        return (
          <>
            <StyledInput
              placeholder="Start IP (e.g., 192.168.1.1)"
              value={rule.startIP || ""}
              onChange={(e) =>
                handleIpRuleChange("startIP", e.target.value, index)
              }
              disabled={!isFormEnabled || isSaving}
            />
            <StyledInput
              placeholder="End IP (e.g., 192.168.1.100)"
              value={rule.endIP || ""}
              onChange={(e) =>
                handleIpRuleChange("endIP", e.target.value, index)
              }
              disabled={!isFormEnabled || isSaving}
            />
          </>
        );
      case "subnet":
        return (
          <StyledInput
            placeholder="Enter Subnet (e.g., 192.168.1.0/24)"
            value={rule.subnet || ""}
            onChange={(e) =>
              handleIpRuleChange("subnet", e.target.value, index)
            }
            disabled={!isFormEnabled || isSaving}
          />
        );
      default:
        return null;
    }
  };

  if (!isFormEnabled || !securityProfileData?.securityprofileID) {
    return (
      <FormContainer id="tour-security-iprules-pane" isFormEnabled={false}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            backgroundColor: "#f5f5f5",
            borderRadius: "8px",
          }}
        >
          <Typography variant="body1" color="textSecondary">
            Please select a security profile to manage IP rules
          </Typography>
        </Box>
      </FormContainer>
    );
  }

  if (!effectiveTenant) {
    return (
      <FormContainer id="tour-security-iprules-pane" isFormEnabled={isFormEnabled}>
        <LoadingContainer>
          <CircularProgress />
          <Alert severity="info">
            Waiting for tenant information...
          </Alert>
        </LoadingContainer>
      </FormContainer>
    );
  }

  if (ipRulesLoading) {
    return (
      <FormContainer id="tour-security-iprules-pane" isFormEnabled={isFormEnabled}>
        <LoadingContainer>
          <CircularProgress />
          <Typography variant="body2">Loading IP rules...</Typography>
        </LoadingContainer>
      </FormContainer>
    );
  }

  if (ipRulesError) {
    return (
      <FormContainer id="tour-security-iprules-pane" isFormEnabled={isFormEnabled}>
        <LoadingContainer>
          <Alert 
            severity="error"
            action={
              <Button onClick={() => refetchIpRules()} size="small">
                Retry
              </Button>
            }
          >
            Failed to load IP rules: {ipRulesError.message}
          </Alert>
        </LoadingContainer>
      </FormContainer>
    );
  }

  const safeIpRules = Array.isArray(ipRules) ? ipRules : [];

  return (
    <FormContainer id="tour-security-iprules-pane" isFormEnabled={isFormEnabled}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "20px",
          gap: 1,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontSize: { xs: 15, sm: 18 },
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
            flex: 1,
            minWidth: 0,
          }}
          title={`IP Rules for ${securityProfileData?.name || "Selected Profile"}`}
        >
          IP Rules for {securityProfileData?.name || "Selected Profile"}
        </Typography>
        <Button
          id="tour-add-iprule-btn"
          variant="contained"
          startIcon={<AddOutlinedIcon />}
          onClick={addIpRule}
          size="small"
          disabled={!isFormEnabled || isSaving}
          sx={{
            whiteSpace: "nowrap",
            minWidth: "auto",
          }}
        >
          {isMobile ? "Add" : "Add IP Rule"}
        </Button>
      </Box>

      <Box
        sx={{
          height: { xs: "auto", md: "calc(100% - 120px)" },
          overflowY: { xs: "visible", md: "auto" },
          paddingRight: "10px",
        }}
      >
        {safeIpRules.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100px",
              backgroundColor: "#f5f5f5",
              borderRadius: "8px",
            }}
          >
            <Typography variant="body2" color="textSecondary" sx={{ textAlign: "center" }}>
              No IP rules defined. Click "Add IP Rule" to create one.
            </Typography>
          </Box>
        ) : (
          safeIpRules.map((rule, index) => (
            <IpRuleBox key={rule.ipRuleID || index}>
              <IpRuleRow style={{ justifyContent: "space-between", width: "100%" }}>
                <FormControl
                  size="small"
                  style={{ minWidth: 120 }}
                >
                  <InputLabel>Type</InputLabel>
                  <Select
                     value={rule.type || "single"}
                     onChange={(e) =>
                       handleIpRuleChange("type", e.target.value, index)
                     }
                     label="Type"
                     disabled={!isFormEnabled || isSaving}
                  >
                    <MenuItem value="single">Single IP</MenuItem>
                    <MenuItem value="range">IP Range</MenuItem>
                    <MenuItem value="subnet">Subnet</MenuItem>
                  </Select>
                </FormControl>
                <IconButton
                   onClick={() => removeIpRule(index)}
                   disabled={!isFormEnabled || isSaving}
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </IpRuleRow>
              <IpRuleRow>{renderIpRuleInputs(rule, index)}</IpRuleRow>
            </IpRuleBox>
          ))
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: { xs: "center", sm: "flex-end" },
          marginTop: "20px",
          marginRight: { xs: "0px", sm: "45px" },
          gap: "10px",
        }}
      >
        <Button
          id="tour-save-iprules-btn"
          variant="contained"
          onClick={saveIpRules}
          disabled={!isFormEnabled || safeIpRules.length === 0 || isSaving}
          startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {isSaving ? "Saving..." : (isMobile ? "Save" : "Save IP Rules")}
        </Button>
        <Button
          variant="outlined"
          onClick={() => {
            setIsFormEnabled(false);
            setSelectedSecurityProfiles([]);
          }}
          disabled={!isFormEnabled || isSaving}
        >
          Cancel
        </Button>
      </Box>
    </FormContainer>
  );
};